/**
 * BuilderCore.ts
 *
 * Core business logic for the Link Builder page.
 * This file extracts all reactive state and methods from Builder.vue,
 * making it reusable across different templates.
 *
 * This follows the Composition API pattern, returning reactive state
 * and methods that can be used by any Vue component.
 */

import { ref, reactive, computed, watch, nextTick } from 'vue';
import { generateLinkUrl } from '$utils/mod.ts';
import { getConfig } from '../config/environment';
import { decodeLinkParams, type LinkParams } from '$utils/decodeLinkParams.ts';

// --- Types ---
export interface BuilderItem {
    description: string;
    quantity: string;
    subtotal: string;
}

export interface InstallmentsConfig {
    installments_active: boolean;
    minimum_amount_terms: string;
    msi_payment_terms: string;
    allow_deferred_payments: boolean;
    minimum_amount_deferred: string;
    deferred_payment_terms: string;
}

export interface EntityInfo {
    name: string;
    address: string;
}

export interface DateRange {
    start: Date | null;
    end: Date | null;
}

export type ToastType = 'success' | 'error';

/**
 * Creates the core builder logic
 */
export function createBuilderCore() {
    const envConfig = getConfig();

    // --- Mode Switch State ---
    const isMerchantMode = ref(false);

    // --- Toast State ---
    const toastVisible = ref(false);
    const toastMessage = ref('');
    const toastType = ref<ToastType>('success');
    const toastTimeout = ref<number | null>(null);

    // --- Core State ---
    // Check environment for default values
    const isDevEnvironment = (): boolean => {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        return hostname.includes('localhost') || hostname.includes('.test');
    };

    const slug = ref(isDevEnvironment() ? 'hotel-demo' : '');

    // Component toggles
    const showReservationSection = ref(true);
    const showItemsSection = ref(false);
    const showPaymentDetailsSection = ref(false);
    const showConfigSection = ref(false);

    // Items management
    const items = ref<BuilderItem[]>([{
        description: '',
        quantity: '',
        subtotal: ''
    }]);

    // Customer Details
    const first_name = ref('');
    const last_name = ref('');
    const email = ref('');
    const phone = ref('');

    // Date Range
    const dateRange = ref<DateRange>(getInitialDates());
    const datePickerKey = ref(0);
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);

    // Reservation Details
    const rooms = ref<number>(1);
    const guests = ref<number>(1);

    // Currency & Amounts
    const subtotal = ref<number>(0);
    const discounts = ref<number>(0);
    const taxes = ref<number>(0);
    const currency = ref('MXN');
    const total = ref<number>(isDevEnvironment() ? 1500 : 0);
    const source_currency = ref('');
    const fx_rate = ref<number | null>(null);
    const previous_amount = ref<number>(0);

    // Config & URLs
    const url_success = ref('');
    const url_failure = ref('');
    const referenceId = ref('');
    const booking_number = ref('');
    const language = ref('es');
    const concept = ref('');
    const disable_inputs = ref(false);
    const disable_invoice = ref(false);

    // Description config
    const description_mode = ref('automatic');
    const custom_description = ref('');

    // Installments config
    const override_installments = ref(false);
    const installments_config = reactive<InstallmentsConfig>({
        installments_active: false,
        minimum_amount_terms: '',
        msi_payment_terms: '',
        allow_deferred_payments: false,
        minimum_amount_deferred: '',
        deferred_payment_terms: ''
    });

    // Output
    const generated_link = ref('');
    const showQrModal = ref(false);
    const qrCacheBuster = ref(0);
    const entityInfo = reactive<EntityInfo>({
        name: '',
        address: ''
    });

    // Control variables
    const isUserInput = ref(true);
    const isDirectTotalInput = ref(false);
    const isDirectPreviousAmountInput = ref(false);

    // Decoder section state
    const decodedOutput = ref('');
    const showDecodeResult = ref(false);

    // --- Helper Functions ---

    function showToast(message: string, type: ToastType = 'success') {
        if (toastTimeout.value !== null) {
            clearTimeout(toastTimeout.value);
        }
        toastMessage.value = message;
        toastType.value = type;
        toastVisible.value = true;
        toastTimeout.value = window.setTimeout(() => {
            toastVisible.value = false;
        }, 3000);
    }

    function getInitialDates(): DateRange {
        const today = new Date();
        const todayDay = today.getDay();

        let nextFriday: Date;
        if (todayDay === 5) {
            nextFriday = new Date(today);
        } else if (todayDay === 6 || todayDay === 0) {
            const daysUntilFriday = todayDay === 6 ? 6 : 5;
            nextFriday = new Date(today);
            nextFriday.setDate(today.getDate() + daysUntilFriday);
        } else {
            const daysUntilFriday = 5 - todayDay;
            nextFriday = new Date(today);
            nextFriday.setDate(today.getDate() + daysUntilFriday);
        }

        const followingSunday = new Date(nextFriday);
        followingSunday.setDate(nextFriday.getDate() + 2);

        const checkinLocal = new Date(
            nextFriday.getFullYear(),
            nextFriday.getMonth(),
            nextFriday.getDate(),
            0, 0, 0, 0
        );
        const checkoutLocal = new Date(
            followingSunday.getFullYear(),
            followingSunday.getMonth(),
            followingSunday.getDate(),
            0, 0, 0, 0
        );

        return { start: checkinLocal, end: checkoutLocal };
    }

    function setupDefaultDates() {
        dateRange.value = getInitialDates();
    }

    // --- Computed Properties ---

    const checkin = computed({
        get: () => dateRange.value.start ? new Date(dateRange.value.start).toISOString() : '',
        set: (value: string) => {
            if (value) {
                dateRange.value.start = new Date(value);
            }
        }
    });

    const checkout = computed({
        get: () => dateRange.value.end ? new Date(dateRange.value.end).toISOString() : '',
        set: (value: string) => {
            if (value) {
                dateRange.value.end = new Date(value);
            }
        }
    });

    const slugLabel = computed(() => isMerchantMode.value ? 'Merchant Slug' : 'Hotel Slug');

    const hasMinimumRequiredFields = computed(() => {
        if (!slug.value) return false;

        if (disable_inputs.value) {
            if (!first_name.value.trim() || !last_name.value.trim() || !email.value.trim() || !phone.value.trim()) {
                return false;
            }
        }

        if (!total.value) return false;

        if (!isMerchantMode.value) {
            if (!checkin.value || !checkout.value) {
                return false;
            }

            const checkinDate = new Date(checkin.value);
            const checkoutDate = new Date(checkout.value);
            if (checkoutDate <= checkinDate) {
                return false;
            }
        }

        if (source_currency.value && currency.value &&
            source_currency.value !== currency.value && !fx_rate.value) {
            return false;
        }

        return true;
    });

    const hasValidInstallmentsConfig = computed(() => {
        if (!override_installments.value) return true;

        if (installments_config.installments_active) {
            if (!installments_config.minimum_amount_terms ||
                !installments_config.msi_payment_terms) {
                return false;
            }
        }

        if (installments_config.allow_deferred_payments) {
            if (!installments_config.minimum_amount_deferred ||
                !installments_config.deferred_payment_terms) {
                return false;
            }
        }

        return true;
    });

    const qrCodeUrl = computed(() => {
        if (!generated_link.value) return '';
        const base = 'https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=';
        const encoded = encodeURIComponent(generated_link.value);
        return `${base}${encoded}&margin=10&_=${qrCacheBuster.value}`;
    });

    const formattedCheckin = computed(() => {
        if (!dateRange.value.start) return '';
        const date = new Date(dateRange.value.start);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}/${mm}/${dd}`;
    });

    const formattedCheckout = computed(() => {
        if (!dateRange.value.end) return '';
        const date = new Date(dateRange.value.end);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}/${mm}/${dd}`;
    });

    const calendarColumns = computed(() => {
        return windowWidth.value >= 1024 ? 2 : 1;
    });

    const availablePreviousCurrencies = computed(() => {
        const currencies = [
            { value: '', text: 'Seleccionar' },
            { value: 'USD', text: 'USD' },
            { value: 'MXN', text: 'MXN' },
            { value: 'EUR', text: 'EUR' }
        ];
        return currencies.filter(curr => curr.value === '' || curr.value !== currency.value);
    });

    const isCurrencyConversionEnabled = computed(() => {
        return source_currency.value && source_currency.value !== currency.value;
    });

    const isTotalReadOnly = computed(() => {
        return showPaymentDetailsSection.value && isCurrencyConversionEnabled.value;
    });

    const entityDisplayName = computed(() => {
        if (entityInfo.name) return entityInfo.name;
        if (slug.value) return titleCaseFromSlug(slug.value);
        return isMerchantMode.value ? 'Comercio' : 'Hotel';
    });

    const entityAddressDisplay = computed(() => entityInfo.address || '');

    const summaryTitle = computed(() => isMerchantMode.value ? 'Resumen de la compra' : 'Resumen de la reserva');

    const summaryRows = computed(() => {
        const rows: Array<{ label: string; value: string }> = [];
        const reference = referenceId.value.trim();
        const booking = booking_number.value.trim();

        if (reference) rows.push({ label: 'Referencia', value: reference });
        if (booking) rows.push({ label: 'Reserva #', value: booking });

        if (!isMerchantMode.value) {
            rows.push({ label: 'Check-in', value: formatDatePreview(checkin.value) });
            rows.push({ label: 'Check-out', value: formatDatePreview(checkout.value) });
            rows.push({ label: 'Habitaciones', value: rooms.value?.toString() || '—' });
            rows.push({ label: 'Huéspedes', value: guests.value?.toString() || '—' });
        } else {
            if (concept.value.trim()) rows.push({ label: 'Concepto', value: concept.value.trim() });
            const validItems = items.value.filter(item => item.description && Number(item.quantity) > 0 && Number(item.subtotal) > 0);
            if (validItems.length) rows.push({ label: 'Ítems', value: String(validItems.length) });
        }

        return rows;
    });

    const summaryTotalDisplay = computed(() => formatCurrencyPreview(total.value || 0, currency.value || 'MXN'));

    const descriptionPreview = computed(() => {
        let desc_parts = [`Reserva en $hotel_name`];

        if (concept.value) {
            desc_parts.push(`[${concept.value}]`);
        }

        if (booking_number.value) {
            desc_parts.push(`[reserva ${booking_number.value}]`);
        }

        if (referenceId.value) {
            desc_parts.push(`ref ${referenceId.value}`);
        }

        const full_description = desc_parts.join(', ');

        if (full_description.length > 200) {
            return full_description.substring(0, 197) + "...";
        }

        return full_description;
    });

    // Dirty state computeds
    const isReservationSectionDirty = computed(() => showReservationSection.value);

    const isItemsSectionDirty = computed(() => {
        return items.value.some(item =>
            item.description.trim() !== '' ||
            Number(item.quantity) > 0 ||
            Number(item.subtotal) > 0
        );
    });

    const isPaymentDetailsSectionDirty = computed(() => {
        return subtotal.value > 0 ||
            discounts.value > 0 ||
            taxes.value > 0 ||
            source_currency.value !== '' ||
            previous_amount.value > 0 ||
            fx_rate.value !== null;
    });

    const isConfigSectionDirty = computed(() => {
        return disable_inputs.value ||
            override_installments.value ||
            description_mode.value !== 'automatic' ||
            custom_description.value !== '';
    });

    // --- URL/Environment ---

    const getCurrentEnv = (): 'local' | 'sandbox' | 'production' => {
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        if (hostname.includes("localhost") || hostname.includes(".test")) return 'local';
        if (hostname.includes("-staging.yopresto.net")) return 'sandbox';
        return 'production';
    };

    const linkBase = computed(() => {
        // Use linkBaseUrl from environment config
        return envConfig.linkBaseUrl;
    });

    const showDecoder = computed(() => {
        const env = getCurrentEnv();
        return env === 'local' || env === 'sandbox';
    });

    const linkInputReadonly = computed(() => !showDecoder.value);

    // --- Utility Functions ---

    function titleCaseFromSlug(value: string): string {
        if (!value) return '';
        return value
            .replace(/[-_]+/g, ' ')
            .split(' ')
            .map(segment => segment ? segment.charAt(0).toUpperCase() + segment.slice(1) : segment)
            .join(' ');
    }

    function formatCurrencyPreview(amount: number | string, currencyCode: string): string {
        if (typeof amount === 'string') amount = parseFloat(amount);
        if (Number.isNaN(amount)) amount = 0;
        const localeMap: Record<string, string> = { MXN: 'es-MX', USD: 'en-US', EUR: 'en-US', CAD: 'en-CA' };
        const symbolMap: Record<string, string> = { MXN: '$', USD: 'US$', EUR: '€', CAD: 'C$' };
        const locale = localeMap[currencyCode] || 'es-MX';
        const symbol = symbolMap[currencyCode] || '$';
        const formatter = new Intl.NumberFormat(locale, {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `${symbol}${formatter.format(Number(amount))} ${currencyCode}`;
    }

    function formatDatePreview(value: string): string {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        const locale = language.value === 'en' ? 'en-US' : language.value === 'fr' ? 'fr-FR' : language.value === 'pt' ? 'pt-BR' : 'es-MX';
        const formatted = date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
        return formatted.toLowerCase();
    }

    // --- Items Management ---

    function addItem() {
        items.value.push({
            description: '',
            quantity: '',
            subtotal: ''
        });
    }

    function removeItem(index: number) {
        if (items.value.length > 1) {
            items.value.splice(index, 1);
            calculateItemsTotal();
        }
    }

    function calculateItemsTotal() {
        const itemsTotal = items.value.reduce((sum, item) => {
            return sum + (Number(item.quantity) * Number(item.subtotal));
        }, 0);
        if (!isDirectTotalInput.value) {
            subtotal.value = itemsTotal;
        }
    }

    // --- Section Clear Functions ---

    function clearReservationSection() {
        first_name.value = '';
        last_name.value = '';
        email.value = '';
        phone.value = '';
        dateRange.value = { start: null, end: null };
        rooms.value = 1;
        guests.value = 1;
        setupDefaultDates();
    }

    function clearItemsSection() {
        items.value = [{
            description: '',
            quantity: '',
            subtotal: ''
        }];
        calculateItemsTotal();
    }

    function clearPaymentDetailsSection() {
        subtotal.value = 0;
        discounts.value = 0;
        taxes.value = 0;
        source_currency.value = '';
        previous_amount.value = 0;
        fx_rate.value = null;
        isDirectPreviousAmountInput.value = false;
    }

    function clearConfigSection() {
        disable_inputs.value = false;
        disable_invoice.value = false;
        override_installments.value = false;
        description_mode.value = 'automatic';
        custom_description.value = '';
        installments_config.installments_active = false;
        installments_config.minimum_amount_terms = '';
        installments_config.msi_payment_terms = '';
        installments_config.allow_deferred_payments = false;
        installments_config.minimum_amount_deferred = '';
        installments_config.deferred_payment_terms = '';
    }

    function clearTextSelection() {
        if (typeof window !== 'undefined' && window.getSelection) {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                selection.removeAllRanges();
            }
        }
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }

    // --- Date Range Handler ---

    function updateDateRange(range: { start: Date | null; end: Date | null }) {
        if (!range || !range.start) {
            console.warn('[Builder] Rango de fechas inválido - no hay fecha de inicio');
            return;
        }

        const startLocal = new Date(
            range.start.getFullYear(),
            range.start.getMonth(),
            range.start.getDate(),
            0, 0, 0, 0
        );

        if (!range.end) {
            const endLocal = new Date(startLocal);
            endLocal.setDate(startLocal.getDate() + 2);
            dateRange.value = { start: startLocal, end: endLocal };
            return;
        }

        const endLocal = new Date(
            range.end.getFullYear(),
            range.end.getMonth(),
            range.end.getDate(),
            0, 0, 0, 0
        );

        const diffDays = Math.floor((endLocal.getTime() - startLocal.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) {
            const correctedEnd = new Date(startLocal);
            correctedEnd.setDate(startLocal.getDate() + 2);
            dateRange.value = { start: startLocal, end: correctedEnd };

            requestAnimationFrame(() => {
                setTimeout(() => {
                    datePickerKey.value++;
                }, 300);
            });

            return;
        }

        dateRange.value = { start: startLocal, end: endLocal };
    }

    // --- Link Generation ---

    function generateLink(): string {
        if (!hasMinimumRequiredFields.value || !hasValidInstallmentsConfig.value) {
            return '';
        }

        const params: Record<string, unknown> = {
            ...(!isMerchantMode.value && showReservationSection.value ? {
                first_name: first_name.value,
                last_name: last_name.value,
                email: email.value,
                phone: phone.value,
                checkin: checkin.value,
                checkout: checkout.value,
                guests: guests.value,
                rooms: rooms.value,
            } : {}),
            currency: currency.value,
            total: total.value,
            ...(showPaymentDetailsSection.value ? {
                subtotal: subtotal.value,
                taxes: taxes.value,
                discounts: discounts.value,
                ...(source_currency.value && source_currency.value !== currency.value ? {
                    source_currency: source_currency.value,
                    fx_rate: fx_rate.value ?? undefined,
                    source_amount: previous_amount.value || undefined,
                } : {}),
            } : {}),
            ...(showItemsSection.value && isItemsSectionDirty.value ? {
                items: items.value.filter(item =>
                    item.description.trim() !== '' &&
                    Number(item.quantity) > 0 &&
                    Number(item.subtotal) > 0
                ).map(item => ({
                    description: item.description.trim(),
                    quantity: Number(item.quantity),
                    price: Number(item.subtotal)
                }))
            } : {}),
            ...(showConfigSection.value ? {
                referenceId: referenceId.value || undefined,
                booking_number: booking_number.value || undefined,
                language: language.value,
                disable_inputs: disable_inputs.value,
                disable_invoice: disable_invoice.value,
                concept: concept.value || undefined,
                ...(description_mode.value === 'custom' && custom_description.value ? {
                    description: custom_description.value
                } : {}),
                url_success: url_success.value || undefined,
                url_failure: url_failure.value || undefined,
                ...(override_installments.value && hasValidInstallmentsConfig.value ? {
                    installments_config: {
                        installments_active: installments_config.installments_active,
                        minimum_amount_terms: installments_config.minimum_amount_terms,
                        msi_payment_terms: installments_config.msi_payment_terms,
                        allow_deferred_payments: installments_config.allow_deferred_payments,
                        minimum_amount_deferred: installments_config.minimum_amount_deferred,
                        deferred_payment_terms: installments_config.deferred_payment_terms
                    }
                } : {}),
            } : {
                language: language.value,
            }),
        };

        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined)
        );

        try {
            const link = generateLinkUrl(
                linkBase.value,
                slug.value,
                filteredParams,
                isMerchantMode.value ? 'merchant' : 'hotel'
            );
            return link;
        } catch (error) {
            console.error("Error generating link:", error);
            showToast('Error al generar el link. Revisa la consola.', 'error');
            return '';
        }
    }

    const computedLink = computed(() => {
        if (hasMinimumRequiredFields.value && hasValidInstallmentsConfig.value) {
            return generateLink();
        }
        return '';
    });

    // --- Actions ---

    function copyLink() {
        if (generated_link.value && generated_link.value.trim() !== '') {
            navigator.clipboard.writeText(generated_link.value)
                .then(() => showToast('Link copiado al portapapeles', 'success'))
                .catch(err => {
                    console.error('Failed to copy link: ', err);
                    showToast('Error al copiar el link', 'error');
                });
        } else {
            showToast('No hay link para copiar. Completa los campos requeridos.', 'error');
        }
    }

    function openLink() {
        if (generated_link.value && generated_link.value.trim() !== '') {
            window.open(generated_link.value, '_blank');
            showToast('Link abierto en nueva pestaña', 'success');
        } else {
            showToast('No hay link para abrir. Completa los campos requeridos.', 'error');
        }
    }

    function openWhatsApp() {
        if (generated_link.value && generated_link.value.trim() !== '') {
            const waUrl = `https://wa.me/?text=${encodeURIComponent(generated_link.value)}`;
            window.open(waUrl, '_blank');
            showToast('Link abierto en WhatsApp Web', 'success');
        } else {
            showToast('No hay link para compartir. Completa los campos requeridos.', 'error');
        }
    }

    function showQrCode() {
        if (generated_link.value && generated_link.value.trim() !== '') {
            qrCacheBuster.value = Date.now();
            showQrModal.value = true;
        } else {
            showToast('No hay link para generar QR. Completa los campos requeridos.', 'error');
        }
    }

    function closeQrModal() {
        showQrModal.value = false;
    }

    // --- Entity Loading ---

    let currentEntityRequest = 0;

    async function loadEntityInfo() {
        const currentSlug = slug.value.trim();
        if (!currentSlug) {
            entityInfo.name = '';
            entityInfo.address = '';
            return;
        }

        if (isMerchantMode.value) {
            entityInfo.name = titleCaseFromSlug(currentSlug);
            entityInfo.address = '';
            return;
        }

        const requestId = ++currentEntityRequest;

        try {
            const response = await fetch(`${envConfig.apiBaseUrl}/hotel-config/${currentSlug}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (currentEntityRequest !== requestId) return;
            const hotelData = data?.data || data;
            entityInfo.name = hotelData?.name || titleCaseFromSlug(currentSlug);
            entityInfo.address = hotelData?.address || '';
        } catch (error) {
            console.error('Error obteniendo información del hotel:', error);
            entityInfo.name = titleCaseFromSlug(currentSlug);
            entityInfo.address = '';
        }
    }

    // --- Link Decoding ---

    /**
     * Decodes a payment link and populates all form fields
     * @param link - The full payment link URL to decode
     * @returns true if decoding was successful, false otherwise
     */
    function decodeAndPopulateFromLink(link: string): boolean {
        if (!link || !link.trim()) {
            return false;
        }

        try {
            const decoded = decodeLinkParams(link);
            if (!decoded) {
                showToast('No se pudo decodificar el link', 'error');
                return false;
            }

            // Show decoded output for debugging in development
            if (showDecoder.value) {
                decodedOutput.value = JSON.stringify(decoded, null, 2);
                showDecodeResult.value = true;
            }

            // Extract slug from URL path
            const url = new URL(link);
            const pathParts = url.pathname.split('/').filter(Boolean);
            if (pathParts.length >= 2) {
                // Path is like /hotel/slug or /merchant/slug
                const mode = pathParts[0];
                const extractedSlug = pathParts[1];

                isMerchantMode.value = mode === 'merchant';
                slug.value = extractedSlug;
            }

            // Populate customer info
            if (decoded.first_name) first_name.value = decoded.first_name;
            if (decoded.last_name) last_name.value = decoded.last_name;
            if (decoded.email) email.value = decoded.email;
            if (decoded.phone) phone.value = decoded.phone;

            // Populate dates
            if (decoded.checkin || decoded.checkout) {
                showReservationSection.value = true;
                if (decoded.checkin) {
                    const checkinDate = new Date(decoded.checkin + 'T00:00:00');
                    dateRange.value.start = checkinDate;
                }
                if (decoded.checkout) {
                    const checkoutDate = new Date(decoded.checkout + 'T00:00:00');
                    dateRange.value.end = checkoutDate;
                }
            }

            // Populate reservation details
            if (decoded.rooms) rooms.value = decoded.rooms;
            if (decoded.guests) guests.value = decoded.guests;

            // Populate amounts
            if (decoded.total) total.value = decoded.total;
            if (decoded.currency) currency.value = decoded.currency;
            if (decoded.subtotal !== undefined || decoded.taxes !== undefined || decoded.discounts !== undefined) {
                showPaymentDetailsSection.value = true;
                if (decoded.subtotal) subtotal.value = decoded.subtotal;
                if (decoded.taxes) taxes.value = decoded.taxes;
                if (decoded.discounts) discounts.value = decoded.discounts;
            }

            // Populate FX info
            if (decoded.source_currency) {
                showPaymentDetailsSection.value = true;
                source_currency.value = decoded.source_currency;
                if (decoded.fx_rate) fx_rate.value = decoded.fx_rate;
                if (decoded.source_amount) previous_amount.value = decoded.source_amount;
            }

            // Populate items
            if (decoded.items && decoded.items.length > 0) {
                showItemsSection.value = true;
                items.value = decoded.items.map(item => ({
                    description: item.description,
                    quantity: String(item.quantity),
                    subtotal: String(item.price)
                }));
            }

            // Populate config section
            if (decoded.referenceId || decoded.booking_number || decoded.url_success ||
                decoded.url_failure || decoded.concept || decoded.disable_inputs !== undefined ||
                decoded.disable_invoice !== undefined) {
                showConfigSection.value = true;
                if (decoded.referenceId) referenceId.value = decoded.referenceId;
                if (decoded.booking_number) booking_number.value = decoded.booking_number;
                if (decoded.url_success) url_success.value = decoded.url_success;
                if (decoded.url_failure) url_failure.value = decoded.url_failure;
                if (decoded.concept) concept.value = decoded.concept;
                if (decoded.disable_inputs !== undefined) disable_inputs.value = decoded.disable_inputs;
                if (decoded.disable_invoice !== undefined) disable_invoice.value = decoded.disable_invoice;
            }

            if (decoded.language) language.value = decoded.language;

            // Populate installments config
            if (decoded.installments_config) {
                showConfigSection.value = true;
                override_installments.value = true;
                Object.assign(installments_config, decoded.installments_config);
            }

            showToast('Link decodificado correctamente', 'success');
            return true;
        } catch (error) {
            console.error('Error decoding link:', error);
            showToast('Error al decodificar el link', 'error');
            return false;
        }
    }

    /**
     * Handles when the link input changes - attempts to decode if it's a valid link
     */
    function handleLinkInputChange(newLink: string) {
        // Only try to decode if it looks like a full URL with params
        if (newLink && newLink.includes('?params=')) {
            decodeAndPopulateFromLink(newLink);
        }
    }

    // --- Setup Watchers ---

    function setupWatchers() {
        // Watch for mode changes
        watch(isMerchantMode, (isMerchant) => {
            if (isMerchant) {
                showReservationSection.value = false;
                showItemsSection.value = true;
            } else {
                showReservationSection.value = true;
                showItemsSection.value = false;
            }
        });

        // Watch items for changes
        watch(items, () => {
            calculateItemsTotal();
        }, { deep: true });

        // Recalculate total when subtotal, discounts, or taxes change
        watch([subtotal, discounts, taxes], () => {
            if (!isDirectTotalInput.value && !isDirectPreviousAmountInput.value) {
                const amount = Number(subtotal.value || 0) - Number(discounts.value || 0) + Number(taxes.value || 0);
                total.value = parseFloat(amount.toFixed(2));
            }
        });

        // Currency conversion
        watch([previous_amount, fx_rate, source_currency, currency], () => {
            if (!source_currency.value || source_currency.value === currency.value) {
                previous_amount.value = 0;
                fx_rate.value = null;
                isDirectPreviousAmountInput.value = false;
                return;
            }

            if (previous_amount.value && fx_rate.value &&
                source_currency.value && currency.value &&
                source_currency.value !== currency.value) {
                const convertedAmount = Number(previous_amount.value) * Number(fx_rate.value);
                isDirectPreviousAmountInput.value = true;
                total.value = parseFloat(convertedAmount.toFixed(2));

                subtotal.value = 0;
                discounts.value = 0;
                taxes.value = 0;

                setTimeout(() => {
                    isDirectPreviousAmountInput.value = false;
                }, 100);
            } else {
                isDirectPreviousAmountInput.value = false;
            }
        });

        // Reset source_currency if same as currency
        watch(currency, (newCurrency) => {
            if (source_currency.value === newCurrency) {
                source_currency.value = '';
            }
        });

        // Auto-fill previous_amount when source_currency is selected
        watch(source_currency, (newCurrency, oldCurrency) => {
            if (newCurrency && newCurrency !== '' && newCurrency !== currency.value && !oldCurrency) {
                if (total.value && total.value > 0) {
                    previous_amount.value = total.value;
                    fx_rate.value = 1;
                }
            }
        });

        // Load entity info when slug changes
        watch([slug, isMerchantMode], () => {
            if (slug.value) {
                loadEntityInfo();
            } else {
                entityInfo.name = '';
                entityInfo.address = '';
            }
        }, { immediate: true });

        // Update generated link
        watch(computedLink, (newValue) => {
            generated_link.value = newValue;
        }, { immediate: true });

        watch(generated_link, (newValue) => {
            if (!newValue) {
                showQrModal.value = false;
            }
        });
    }

    // Initialize watchers immediately
    setupWatchers();

    // --- Return Public API ---

    return {
        // Config
        envConfig,

        // Mode
        isMerchantMode,

        // Toast
        toastVisible,
        toastMessage,
        toastType,
        showToast,

        // Core state
        slug,
        showReservationSection,
        showItemsSection,
        showPaymentDetailsSection,
        showConfigSection,

        // Items
        items,
        addItem,
        removeItem,
        calculateItemsTotal,

        // Customer
        first_name,
        last_name,
        email,
        phone,

        // Dates
        dateRange,
        datePickerKey,
        windowWidth,
        checkin,
        checkout,
        formattedCheckin,
        formattedCheckout,
        calendarColumns,
        updateDateRange,
        setupDefaultDates,

        // Reservation
        rooms,
        guests,

        // Amounts
        subtotal,
        discounts,
        taxes,
        currency,
        total,
        source_currency,
        fx_rate,
        previous_amount,
        availablePreviousCurrencies,
        isCurrencyConversionEnabled,
        isTotalReadOnly,

        // Config
        url_success,
        url_failure,
        referenceId,
        booking_number,
        language,
        concept,
        disable_inputs,
        disable_invoice,
        description_mode,
        custom_description,
        descriptionPreview,

        // Installments
        override_installments,
        installments_config,

        // Output
        generated_link,
        showQrModal,
        qrCacheBuster,
        qrCodeUrl,
        computedLink,

        // Entity
        entityInfo,
        entityDisplayName,
        entityAddressDisplay,

        // Summary
        summaryTitle,
        summaryRows,
        summaryTotalDisplay,

        // Computed
        slugLabel,
        hasMinimumRequiredFields,
        hasValidInstallmentsConfig,

        // Dirty state
        isReservationSectionDirty,
        isItemsSectionDirty,
        isPaymentDetailsSectionDirty,
        isConfigSectionDirty,

        // Section clear
        clearReservationSection,
        clearItemsSection,
        clearPaymentDetailsSection,
        clearConfigSection,
        clearTextSelection,

        // URL/Environment
        getCurrentEnv,
        linkBase,
        showDecoder,
        linkInputReadonly,

        // Utils
        titleCaseFromSlug,
        formatCurrencyPreview,
        formatDatePreview,

        // Actions
        generateLink,
        copyLink,
        openLink,
        openWhatsApp,
        showQrCode,
        closeQrModal,

        // Entity
        loadEntityInfo,

        // Decoder
        decodedOutput,
        showDecodeResult,
        decodeAndPopulateFromLink,
        handleLinkInputChange,

        // Control
        isUserInput,
        isDirectTotalInput,
        isDirectPreviousAmountInput,

        // Setup
        setupWatchers,
    };
}

export type BuilderCore = ReturnType<typeof createBuilderCore>;
